import {BellIcon, Bars3Icon, ChevronDownIcon, MagnifyingGlassIcon, UserIcon} from "@heroicons/react/16/solid";
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  FlyoutButton,
  FlyoutMenu,
  FlyoutPanel,
  Navbar,
  NavbarDivider,
  NavbarItem,
  NavbarLabel,
  NavbarSection,
} from "catalyst";
import {Fragment, ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import {ConnectedProcessTray, ConnectedProcessTrayProps} from "../processes/ConnectedProcessTray";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot, useOtsBrand, useOtsColorScheme} from "../theme/OtsTheme";
import {getOtsThemeVariables} from "../theme/otsThemeTokens";
import {flyoutLinks, OtsNavCount, OtsNavFlyout, OtsNavFlyoutMenu, OtsNavLink} from "./OtsNavFlyout";
import {OtsStatusStrip, OtsStatusStripProps} from "./OtsStatusStrip";
import {useOtsNavPins} from "./useOtsNavPins";

export type OtsAppNavbarLink = OtsNavLink;

/**
 * A top-level destination: a place to go, or a group that opens a flyout. `children` is the short form of a flyout
 * with one untitled column; `flyout` gives titled columns, shortcuts, and a footer, as the dashboard's do.
 */
export type OtsAppNavbarItem = Omit<OtsNavLink, "onClick" | "description" | "disabled"> & {
  onClick?: () => void;
  children?: OtsNavLink[];
  flyout?: OtsNavFlyoutMenu;
  /** Marks the item for an onboarding tour. */
  onboardingId?: string;
};

export type OtsAppNavbarMenuItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  description?: string;
  disabled?: boolean;
  onClick: () => void;
  /** Marks the current choice among alternatives, e.g. the theme in use. */
  selected?: boolean;
  /** Draws a rule above the item to start a new group. */
  separated?: boolean;
};

export type OtsAppNavbarProps = {
  /** The product mark, at the far left. */
  logo: ReactNode;
  /** Right after the logo: what the whole app is working in, e.g. the category switcher. */
  context?: ReactNode;
  /** After the context, e.g. the region switcher. */
  start?: ReactNode;
  /** Primary destinations; secondary links belong in their flyouts. */
  items: OtsAppNavbarItem[];
  /** Require a wider host before expanding destinations, for longer navigation bars (1440px vs 1024px). */
  wideNavigation?: boolean;
  /** Id of the destination the person is on: a top-level item or anything in its flyout. */
  currentId?: string;
  /** Marks the destinations for an onboarding tour. */
  itemsOnboardingId?: string;
  /** Draws each destination's icon beside its label; off, the bar shows labels alone. */
  itemIcons?: boolean;
  /** The person's background work, e.g. reports being built. Left out, there is no processes tray. */
  processes?: Omit<ConnectedProcessTrayProps, "triggerAs" | "brand" | "colorScheme">;
  /** Opens the app's search; left out, there is no search button. */
  onSearch?: () => void;
  /** The notices bell; left out for people who can't see notices. */
  notices?: {count?: number; onClick: () => void};
  /** Other icon-only actions, after notices, e.g. chat. */
  actions?: OtsNavLink[];
  /** Sits between the actions and the account menu, e.g. the category switcher. */
  end?: ReactNode;
  /** Who is signed in, for the account menu. */
  user?: {name: string; details?: string[]};
  /** Whether to offer Sign out or Sign in; defaults to whether there is a `user`, for hosts that know before the person loads. */
  signedIn?: boolean;
  userMenuItems?: OtsAppNavbarMenuItem[];
  onSignOut?: () => void;
  onSignIn?: () => void;
  /** The strip under the bar; omitted when missing and nothing is pinned. */
  status?: OtsStatusStripProps;
  /**
   * Lets the person pin flyout destinations to the status strip, remembered in this browser under this key (one per
   * app, e.g. "hub-go"). Left out, there are no pins.
   */
  pinKey?: string;
  /** Most destinations pinned at once. */
  maxPins?: number;
  /** The bar's height with its strip, for a host shell that pads its page by the header's height. */
  onHeightChange?: (height: number) => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  className?: string;
};

/** Above a host shell's fixed header (Mantine's is 100) and its own menus. */
const menuLayer = "z-[1001]";

const menuItemClass = [
  "!rounded-md !px-2.5 !py-1 !text-xs/5",
  "*:data-[slot=icon]:!mr-1.5 *:data-[slot=icon]:!size-3.5",
  "*:data-[slot=description]:!text-[0.6875rem]/4",
  "data-focus:!bg-[var(--ots-primary)] data-focus:!text-[var(--ots-on-primary)]",
  "data-focus:*:data-[slot=icon]:!text-[var(--ots-on-primary)]",
].join(" ");

/**
 * Where you are: a brand-tinted item with its label and icon in the brand color, over catalyst's underline
 * indicator (the wrapper's first span, present only when current) in the OTS accent, so it reads even on
 * DispatchGo's graphite.
 */
/** Text from the OTS theme rather than catalyst's `dark:` variant, so the bar reads in a dark theme root anywhere. */
const textClass = "[&>*:last-child]:text-[var(--ots-text,#09090b)]";

const itemClass = [
  textClass,
  "[&>span:first-child]:!bg-[var(--ots-accent,#E1251B)]",
  "[&>[data-current]]:!bg-[color-mix(in_srgb,var(--ots-primary,#18181b)_12%,transparent)]",
  "[&>[data-current]]:!font-semibold [&>[data-current]]:!text-[var(--ots-primary,#18181b)]",
  "[&>[data-current]_[data-slot=icon]]:!fill-[var(--ots-primary,#18181b)]",
].join(" ");

const flyoutOf = (item: OtsAppNavbarItem): OtsNavFlyoutMenu | undefined =>
  item.flyout ?? (item.children?.length ? {columns: [{items: item.children}]} : undefined);

const sum = (links: OtsNavLink[]) => links.reduce((total, link) => total + (link.badgeCount ?? 0), 0);

const MenuLinks = ({links, currentId}: {links: OtsNavLink[]; currentId?: string}) => <>
  {links.map(link => (
    <DropdownItem key={link.id} className={menuItemClass} onClick={link.onClick} disabled={link.disabled} aria-current={link.id === currentId ? "page" : undefined}>
      {link.icon}
      <DropdownLabel className={link.id === currentId ? "font-semibold text-[var(--ots-primary)]" : undefined}>
        {link.label}
        {!!link.badgeCount && <OtsNavCount count={link.badgeCount} className="ml-2 align-middle"/>}
      </DropdownLabel>
      {link.description && <DropdownDescription>{link.description}</DropdownDescription>}
    </DropdownItem>
  ))}
</>;

/** Labels in Inter, as ots-site's header has them; set here because hosts and portals don't pass it down. */
const fontStyle = {fontFamily: "var(--ots-font-body)"};

const Bar = ({
  logo,
  context,
  start,
  items,
  wideNavigation = false,
  currentId,
  itemsOnboardingId,
  itemIcons = false,
  processes,
  onSearch,
  notices,
  actions = [],
  end,
  user,
  userMenuItems = [],
  signedIn = !!user,
  onSignOut,
  onSignIn,
  status,
  pinKey,
  maxPins = 4,
  onHeightChange,
  className,
}: OtsAppNavbarProps) => {
  const brand = useOtsBrand() ?? "corporate";
  const colorScheme = useOtsColorScheme() ?? "light";
  // Menus and flyouts portal out of the bar, so they carry its theme with them.
  const themeVariables = {...getOtsThemeVariables(brand, colorScheme), ...fontStyle};

  const root = useRef<HTMLDivElement>(null);
  const heightChanged = useRef(onHeightChange);
  heightChanged.current = onHeightChange;
  // Flyouts span the window just under the bar (not its status strip), wherever the host has put it.
  const [flyoutTop, setFlyoutTop] = useState(64);
  const measure = useCallback(() => {
    const nav = root.current?.querySelector("nav");
    if (nav) setFlyoutTop(Math.round(nav.getBoundingClientRect().bottom));
  }, []);
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const update = () => {
      measure();
      heightChanged.current?.(Math.ceil(element.getBoundingClientRect().height));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  // What can be pinned: the destinations in flyouts (top-level ones are already in the bar).
  const pinnable = useMemo(() => items
    .flatMap(item => {
      const flyout = flyoutOf(item);
      return flyout ? flyoutLinks(flyout) : [];
    })
    .filter(link => !link.disabled), [items]);
  const pinnableIds = useMemo(() => pinnable.map(link => link.id), [pinnable]);
  const pins = useOtsNavPins(pinKey, pinnableIds, maxPins);
  const pinned = pins.pinned.flatMap(id => {
    const link = pinnable.find(candidate => candidate.id === id);
    return link ? [{
      id,
      label: link.label,
      icon: link.icon,
      current: id === currentId,
      onClick: link.onClick,
      onUnpin: () => pins.unpin(id),
    }] : [];
  });

  const isCurrent = (item: OtsAppNavbarItem) => {
    const flyout = flyoutOf(item);
    return item.id === currentId || (!!flyout && flyoutLinks(flyout).some(link => link.id === currentId));
  };

  return (
    <div
      ref={root}
      style={fontStyle}
      className={["@container relative w-full bg-[var(--ots-surface,#fff)] text-[var(--ots-text,#09090b)] [&_button]:[font-family:inherit]", className].filter(Boolean).join(" ")}>
      {/* `!flex`: host resets (Mantine's) set `nav { display: block }` outside any CSS layer, which would stack the bar. */}
      <Navbar aria-label="Primary" className="!flex px-3">
        {items.length > 0 && (
          <div className={wideNavigation ? "@[90rem]:hidden" : "@5xl:hidden"}>
            <Dropdown>
              <DropdownButton as={NavbarItem} className={textClass} aria-label="Open navigation menu">
                <Bars3Icon/>
              </DropdownButton>
              <DropdownMenu anchor="bottom start" className={`${menuLayer} min-w-64`} style={themeVariables}>
                {items.map((item, index) => {
                  const flyout = flyoutOf(item);
                  return (
                    <Fragment key={item.id}>
                      {index > 0 && <DropdownDivider/>}
                      {flyout
                        ? <>
                          <DropdownHeader><span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{item.label}</span></DropdownHeader>
                          <MenuLinks links={flyoutLinks(flyout)} currentId={currentId}/>
                        </>
                        : <MenuLinks links={[{...item, icon: itemIcons ? item.icon : undefined, onClick: item.onClick ?? (() => undefined)}]} currentId={currentId}/>}
                    </Fragment>
                  );
                })}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
        <div className="flex h-10 shrink-0 items-center px-1">{logo}</div>
        {context != null && <>
          <NavbarDivider className="shrink-0"/>
          <div className="flex shrink-0 items-center">{context}</div>
        </>}
        {start != null && <>
          <NavbarDivider className="shrink-0 max-lg:hidden"/>
          <div className="flex shrink-0 items-center">{start}</div>
        </>}
        {items.length > 0 && <>
          <NavbarDivider className={wideNavigation ? "hidden shrink-0 @[90rem]:block" : "hidden shrink-0 @5xl:block"}/>
          <NavbarSection className={wideNavigation ? "hidden min-w-0 @[90rem]:flex" : "hidden min-w-0 @5xl:flex"} data-onboarding-id={itemsOnboardingId}>
            {items.map(item => {
              const flyout = flyoutOf(item);
              return flyout
                ? <FlyoutMenu key={item.id} className="static">
                  {({close}) => <>
                    <FlyoutButton
                      as={NavbarItem}
                      className={itemClass}
                      current={isCurrent(item)}
                      onPointerDown={measure}
                      data-onboarding-id={item.onboardingId}>
                      {itemIcons && item.icon}
                      <NavbarLabel>{item.label}</NavbarLabel>
                      <OtsNavCount count={(item.badgeCount ?? 0) + sum(flyoutLinks(flyout))}/>
                      <ChevronDownIcon/>
                    </FlyoutButton>
                    <FlyoutPanel
                      viewport
                      viewportTop={flyoutTop}
                      style={themeVariables}
                      className={`!z-[1001] !bg-[color-mix(in_srgb,var(--ots-surface)_95%,transparent)] text-[var(--ots-text)]`}>
                      <OtsNavFlyout menu={flyout} currentId={currentId} pins={pinKey ? pins : undefined} close={close}/>
                    </FlyoutPanel>
                  </>}
                </FlyoutMenu>
                : <NavbarItem
                  key={item.id}
                  className={itemClass}
                  current={isCurrent(item)}
                  aria-current={isCurrent(item) ? "page" : undefined}
                  onClick={item.onClick}
                  data-onboarding-id={item.onboardingId}>
                  {itemIcons && item.icon}
                  <NavbarLabel>{item.label}</NavbarLabel>
                  <OtsNavCount count={item.badgeCount}/>
                </NavbarItem>;
            })}
          </NavbarSection>
        </>}
        <NavbarSection className="ml-auto shrink-0">
          {processes && user && (
            <ConnectedProcessTray {...processes} triggerAs={NavbarItem} iconOnly brand={brand} colorScheme={colorScheme}/>
          )}
          {onSearch && (
            <NavbarItem className={textClass} aria-label="Search" title="Search" onClick={onSearch}>
              <MagnifyingGlassIcon/>
            </NavbarItem>
          )}
          {notices && (
            <NavbarItem className={textClass} aria-label="Notices" title="Notices" onClick={notices.onClick}>
              <BellIcon/>
              <OtsNavCount count={notices.count}/>
            </NavbarItem>
          )}
          {actions.map(action => (
            <NavbarItem key={action.id} className={textClass} aria-label={action.label} title={action.label} onClick={action.onClick}>
              {action.icon}
              <OtsNavCount count={action.badgeCount}/>
            </NavbarItem>
          ))}
          {end != null && <div className="mx-1 flex shrink-0 items-center">{end}</div>}
          {user && <NavbarDivider className="shrink-0 max-lg:hidden"/>}
          {user && (
            <Dropdown>
              <DropdownButton as={NavbarItem} className={textClass} aria-label={`Account menu for ${user.name}`}>
                <UserIcon/>
                <NavbarLabel className="hidden max-w-40 @[100rem]:inline">{user.name}</NavbarLabel>
                <ChevronDownIcon/>
              </DropdownButton>
              <DropdownMenu className={`${menuLayer} min-w-64`} anchor="bottom end" style={themeVariables}>
                <DropdownHeader>
                  <div className="pr-6">
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">{`Signed in as ${user.name}`}</div>
                    {user.details?.map(detail => (
                      <div key={detail} className="text-sm/7 font-semibold text-zinc-800 dark:text-white">{detail}</div>
                    ))}
                  </div>
                </DropdownHeader>
                {userMenuItems.map((item, index) => (
                  <Fragment key={item.id}>
                    {(item.separated || index === 0) && <DropdownDivider/>}
                    <DropdownItem className={menuItemClass} onClick={item.onClick} disabled={item.disabled} aria-checked={item.selected}>
                      {item.icon}
                      <DropdownLabel className={item.selected ? "font-semibold" : undefined}>{item.label}{item.selected ? " ✓" : ""}</DropdownLabel>
                      {item.description && <DropdownDescription>{item.description}</DropdownDescription>}
                    </DropdownItem>
                  </Fragment>
                ))}
              </DropdownMenu>
            </Dropdown>
          )}
          {(signedIn ? onSignOut : onSignIn) && <>
            <NavbarDivider className="shrink-0 max-lg:hidden"/>
            <NavbarItem className={`${textClass} shrink-0 whitespace-nowrap`} onClick={signedIn ? onSignOut : onSignIn}>
              {signedIn ? "Sign out" : "Sign in"}
            </NavbarItem>
          </>}
        </NavbarSection>
      </Navbar>
      {/* Part of the bar rather than a band under it: the bar's own surface, set off only by a dashed rule. */}
      {(status || pinned.length > 0) && <OtsStatusStrip
        {...status}
        pinned={pinned}
        className="!border-dashed !border-[var(--ots-outline-variant,rgb(9_9_11/0.1))] !bg-transparent"/>}
    </div>
  );
};

/**
 * An application's bar across the top, built to the dashboard's standard: the product mark, what the app is
 * working in (category, region), its destinations
 * (groups open a compact full-width flyout), the person's processes, search, notices, the account menu and
 * sign out, over a status strip. The destination the person is on is drawn in the brand color. Colors come
 * from the OTS theme, so it is light or dark with the app. When the bar is too narrow for its destinations they
 * fold into one menu.
 */
export const OtsAppNavbar = ({brand, colorScheme, ...props}: OtsAppNavbarProps) => (
  <OtsThemeRoot brand={brand} colorScheme={colorScheme} className="contents">
    <Bar {...props}/>
  </OtsThemeRoot>
);
